import {
  AutocompleteChangeReason,
  Box,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import { Field, Form, Formik } from "formik";
import { useDispatch, useSelector } from "../../store";
import * as Yup from "yup";
import { editCategory, postCategory } from "../../slices/category";
import { Inputs } from "../formInputs";
import { searchJobTypes } from "../../slices/jobType";
import { Base } from "../base";
import { debounce } from "lodash";
import { defaultParams } from "../../types/searchParams";

interface Props {
  close: () => void;
  onSave: () => void;
}

const AddEditCategoryForm: React.FC<Props> = (props) => {
  const { close, onSave } = props;
  const { category, status } = useSelector((store) => store.category);
  const { jobTypeSearchResults } = useSelector((store) => store.jobType);
  const dispatch = useDispatch();
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
    jobType: Yup.object().required("Required").typeError("Selection Required"),
  });

  useEffect(() => {
    dispatch(searchJobTypes({ query: "" }));
  }, [dispatch]);

  const handleSubmit = (values: any) => {
    if (values.id === 0) {
      dispatch(postCategory({ id: 0, ...values })).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          updateAfterChanged();
        }
      });
    } else {
      dispatch(editCategory(values)).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          updateAfterChanged();
        }
      });
    }
  };

  const updateAfterChanged = () => {
    close();
    if (xs) {
      onSave();
    }
  };

  const updateSearch = debounce((query: string) => {
    dispatch(searchJobTypes({ query: query }));
  }, 500);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        width: 300,
        borderRadius: "10px",
        padding: "15px",
      }}
    >
      <Formik
        initialValues={category}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        <Form noValidate>
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <Field
                component={Inputs.Text}
                name="name"
                label="Category Name"
              />
            </Grid>
            {category.id === 0 && (
              <Grid size={{ xs: 12 }}>
                <Field
                  component={Inputs.Dropdown}
                  name="jobType"
                  label="Job Type Name"
                  options={jobTypeSearchResults?.results ?? []}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    updateSearch(e.currentTarget.value);
                  }}
                  onFocus={() => dispatch(searchJobTypes(defaultParams))}
                  onSelected={(
                    e: React.SyntheticEvent,
                    value: any,
                    reason: AutocompleteChangeReason
                  ) => {
                    if (reason === "clear") {
                      dispatch(searchJobTypes(defaultParams));
                    }
                  }}
                />
              </Grid>
            )}
          </Grid>
          <Grid container direction={"row"} justifyContent={"space-between"}>
            <Grid>
              <Base.Button text="Cancel" type="secondary" onClick={close} />
            </Grid>
            <Grid>
              <Inputs.Submit
                isSubmitting={status === "loading"}
                text={"Save"}
              />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Box>
  );
};

export default AddEditCategoryForm;
